import { CONSENT_MODES, CONSENT_STATUS, DIDOMI_CONSENT_CHANGED_EVENT } from './constants';

// Didomi is loaded site wide by IndexPage, callbacks wait on `didomiOnReady`.
// https://developers.didomi.io/cmp/web-sdk/reference/api
const onDidomiReady = callback => {
  window.didomiOnReady = window.didomiOnReady || [];
  window.didomiOnReady.push(callback);
};

const allEnabled = (ids, group = {}) => ids.every(id => group[id] && group[id].enabled === true);

const noop = () => {};

export const hasDidomiIds = ({ purposes = [], vendors = [] } = {}) =>
  purposes.length > 0 || vendors.length > 0;

/**
 * Consent status for PostHog from Didomi: pending while the user has not made a choice, granted
 * when every configured purpose and vendor is enabled, denied otherwise. With nothing configured
 * it stays pending, so nothing is captured: a missing purpose id must not become "consent given",
 * nor allow cookieless capture of everyone
 */
export const getDidomiStatus = (Didomi, { purposes = [], vendors = [] } = {}) => {
  if (!hasDidomiIds({ purposes, vendors })) return CONSENT_STATUS.PENDING;

  try {
    if (
      typeof Didomi.shouldConsentBeCollected === 'function' &&
      Didomi.shouldConsentBeCollected()
    ) {
      return CONSENT_STATUS.PENDING;
    }

    const status = Didomi.getCurrentUserStatus() || {};
    return allEnabled(purposes, status.purposes) && allEnabled(vendors, status.vendors)
      ? CONSENT_STATUS.GRANTED
      : CONSENT_STATUS.DENIED;
  } catch (error) {
    // a Didomi failure is not a choice, so nothing is captured until Didomi can report one
    return CONSENT_STATUS.PENDING;
  }
};

/**
 * Calls onChange with the consent status now and whenever it changes, returns an unsubscribe.
 * If Didomi never loads the status stays pending, so PostHog captures nothing
 * (cookieless_mode: 'on_reject' waits for a choice).
 */
export default function subscribeToConsent(consent, onChange) {
  const { mode, ...options } = consent || {};

  if (mode === CONSENT_MODES.ASSUMED) {
    onChange(CONSENT_STATUS.GRANTED);
    return noop;
  }

  // explicitly always denied: opted out, cookieless capture only with captureOnReject
  if (mode === CONSENT_MODES.DISABLED) {
    onChange(CONSENT_STATUS.DENIED);
    return noop;
  }

  // unknown modes fail closed: pending, so nothing is captured
  if (mode !== CONSENT_MODES.DIDOMI) {
    onChange(CONSENT_STATUS.PENDING);
    return noop;
  }

  let active = true;
  let didomi = null;
  const update = () => {
    if (active && didomi) onChange(getDidomiStatus(didomi, options));
  };

  onDidomiReady(Didomi => {
    if (!active) return;
    didomi = Didomi;
    update();
    Didomi.on(DIDOMI_CONSENT_CHANGED_EVENT, update);
  });

  return () => {
    active = false;
    if (didomi && typeof didomi.off === 'function') {
      didomi.off(DIDOMI_CONSENT_CHANGED_EVENT, update);
    }
  };
}
